import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Download, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { facultyExamApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { checkPdfFile } from '../../lib/uploadLimits';
import UploadHint from '../../components/UploadHint';
import PdfExamViewer from '../../components/PdfExamViewer';
import { parseApiError } from '../../lib/apiErrors';

function DeleteSubmissionModal({ attemptNumber, deleting, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-heading font-bold text-slate-900 dark:text-white">
          Delete Submission {attemptNumber}?
        </h3>
        <p className="mb-5 text-sm text-slate-600 dark:text-slate-300">
          This permanently deletes the uploaded file for Submission {attemptNumber}. The
          submission record stays on the exam's history — this only frees the storage the
          file was using. This cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : `Delete Submission ${attemptNumber}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EvaluationDetailPage() {
  const { examId, attemptId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);

  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const blobUrlRef = useRef(null);

  const [marksAwarded, setMarksAwarded] = useState('');
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);

  const [reviewedFile, setReviewedFile] = useState(null);
  const [uploadingReviewed, setUploadingReviewed] = useState(false);

  const [violationsOpen, setViolationsOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null); // { id, index }
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);

  // Revoke the current submission blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [examRes, subsRes] = await Promise.all([
        facultyExamApi.getExam(examId),
        facultyExamApi.getExamSubmissions(examId),
      ]);
      setExam(examRes.data);

      const found = subsRes.data.find((a) => a.attempt_id === attemptId);
      if (!found) {
        setLoadError("This attempt was not found among the exam's submissions.");
        setAttempt(null);
        setLoading(false);
        return;
      }
      setAttempt(found);
      setMarksAwarded(found.total_score != null ? String(found.total_score) : '');

      const subs = found.submissions || [];
      setSelectedSubmissionId((current) => {
        const currentIsLive = current && subs.some((s) => s.id === current && !s.is_deleted);
        if (currentIsLive) return current;
        const liveSubs = subs.filter((s) => !s.is_deleted);
        return liveSubs.length > 0 ? liveSubs[liveSubs.length - 1].id : null;
      });

      // The submissions list doesn't carry feedback text (only marks + has_corrected_paper),
      // so pre-filling feedback for a re-grade needs a second, best-effort lookup via the ledger.
      setFeedback('');
      if (found.student_id) {
        try {
          const ledgerRes = await facultyExamApi.getStudentLedger(found.student_id);
          const ledgerRow = (ledgerRes.data.attempts || []).find((a) => a.attempt_id === attemptId);
          if (ledgerRow?.feedback) {
            setFeedback(ledgerRow.feedback);
          }
        } catch {
          // non-critical — leave feedback blank if the ledger lookup fails
        }
      }
    } catch (err) {
      setLoadError(parseApiError(err, 'Failed to load this evaluation.').message);
      setAttempt(null);
    } finally {
      setLoading(false);
    }
  }, [examId, attemptId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadSubmissionBlob = useCallback(async (submissionId) => {
    setPdfLoading(true);
    setPdfLoadError(false);
    try {
      const res = await facultyExamApi.downloadSubmission(submissionId);
      const contentType = res.headers?.['content-type'] || 'application/pdf';
      const blob = new Blob([res.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = url;
      setPdfBlobUrl(url);
    } catch {
      setPdfLoadError(true);
      setPdfBlobUrl(null);
    } finally {
      setPdfLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSubmissionId) {
      loadSubmissionBlob(selectedSubmissionId);
    } else {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPdfBlobUrl(null);
    }
  }, [selectedSubmissionId, loadSubmissionBlob]);

  const handleAuthDownload = async (downloadFn, filename) => {
    try {
      const response = await downloadFn();
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(parseApiError(err, 'Download failed').message);
    }
  };

  const handleDeleteSubmission = async () => {
    if (!deleteTarget) return;
    setDeletingSubmissionId(deleteTarget.id);
    try {
      await facultyExamApi.deleteSubmission(deleteTarget.id);
      toast.success(`Submission ${deleteTarget.index + 1} deleted`);
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete submission').message);
    } finally {
      setDeletingSubmissionId(null);
    }
  };

  const handleSaveEvaluation = async () => {
    const marksNum = parseFloat(marksAwarded);
    const maxMarks = attempt?.total_marks ?? Infinity;
    if (Number.isNaN(marksNum) || marksNum < 0 || marksNum > maxMarks) {
      toast.error(`Marks must be between 0 and ${attempt?.total_marks ?? '—'}`);
      return;
    }
    setSaving(true);
    try {
      await facultyExamApi.evaluateSubmission(attemptId, {
        marks_awarded: marksNum,
        feedback: feedback.trim() || null,
      });
      toast.success('Evaluation saved');
      await loadData();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to save evaluation').message);
    } finally {
      setSaving(false);
    }
  };

  const handleReviewedFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    const sizeError = checkPdfFile(file, 'Corrected paper');
    if (sizeError) {
      toast.error(sizeError);
      event.target.value = '';
      return;
    }
    setReviewedFile(file);
  };

  const handleUploadReviewed = async () => {
    if (!reviewedFile) {
      toast.error('Choose a file to upload first');
      return;
    }
    setUploadingReviewed(true);
    try {
      const formData = new FormData();
      formData.append('file', reviewedFile);
      await facultyExamApi.uploadReviewedPaper(attemptId, formData);
      toast.success('Corrected paper uploaded');
      setReviewedFile(null);
      await loadData();
    } catch (err) {
      toast.error(parseApiError(err, 'Upload failed').message);
    } finally {
      setUploadingReviewed(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Evaluation">
        <div className="space-y-4 animate-pulse">
          <div className="h-24 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-96 rounded-lg bg-slate-200 dark:bg-slate-700" />
        </div>
      </DashboardLayout>
    );
  }

  if (loadError || !attempt) {
    return (
      <DashboardLayout title="Evaluation">
        <div className="space-y-4">
          <button
            onClick={() => navigate(`/faculty/exams/${examId}`)}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-primary dark:text-slate-400 dark:hover:text-accent-cool"
          >
            <ArrowLeft size={18} />
            Back to Exam
          </button>
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-300">
            {loadError || 'Attempt not found.'}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const submissions = attempt.submissions || [];
  const violationEntries = attempt.violation_details ? Object.entries(attempt.violation_details) : [];

  return (
    <DashboardLayout title="Evaluation">
      <div className="max-w-6xl animate-fade-in space-y-6">
        <button
          onClick={() => navigate(`/faculty/exams/${examId}`)}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-primary dark:text-slate-400 dark:hover:text-accent-cool"
        >
          <ArrowLeft size={18} />
          Back to Exam
        </button>

        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">{attempt.student_name}</h2>
              <p className="text-slate-500 dark:text-slate-400">
                {attempt.student_class || 'Class not provided'} | {attempt.phone_number || 'Phone not provided'}
              </p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {exam?.title || attempt.exam_title}
                {exam?.subject ? ` · ${exam.subject}` : ''}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Submitted: {attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : 'Unknown'}
              </p>
            </div>
            <div className="flex flex-col items-start gap-2 lg:items-end">
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  attempt.total_score != null
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                }`}
              >
                {attempt.total_score != null ? `Graded: ${attempt.total_score}/${attempt.total_marks}` : 'Ungraded'}
              </span>
              <button
                onClick={() => setViolationsOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline dark:text-amber-300"
              >
                Violations: {attempt.violation_count || 0}
                {violationEntries.length > 0 && (violationsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </button>
              {violationsOpen && violationEntries.length > 0 && (
                <div className="max-w-xs rounded-md bg-amber-50 p-2 text-right text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  {violationEntries.map(([key, value]) => (
                    <div key={key}>
                      {key}: {value}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <div className="mb-3 flex flex-wrap gap-2">
              {submissions.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">No submitted files for this attempt.</p>
              ) : (
                submissions.map((s, i) => (
                  <div
                    key={s.id}
                    className={`flex items-center gap-1 rounded-md ${
                      s.is_deleted
                        ? 'bg-slate-100 opacity-60 dark:bg-slate-800'
                        : selectedSubmissionId === s.id
                        ? 'bg-primary'
                        : 'bg-slate-100 dark:bg-slate-700'
                    }`}
                  >
                    <button
                      onClick={() => !s.is_deleted && setSelectedSubmissionId(s.id)}
                      disabled={s.is_deleted}
                      className={`px-3 py-1.5 text-sm ${
                        s.is_deleted
                          ? 'cursor-not-allowed text-slate-400 dark:text-slate-500'
                          : selectedSubmissionId === s.id
                          ? 'text-white'
                          : 'text-slate-700 hover:bg-slate-200 dark:text-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      Submission {i + 1} — {new Date(s.submitted_at).toLocaleString()}
                      {s.is_late ? ' (late)' : ''}
                      {s.is_deleted ? ' (file deleted)' : ''}
                    </button>
                    {!s.is_deleted && attempt.total_score == null && (
                      <button
                        onClick={() => setDeleteTarget({ id: s.id, index: i })}
                        title={`Delete Submission ${i + 1}`}
                        className="mr-1 rounded p-1 text-slate-400 hover:bg-red-100 hover:text-red-600 dark:text-slate-300 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {selectedSubmissionId ? (
              pdfLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : pdfLoadError ? (
                <p className="py-16 text-center text-sm text-red-500">Failed to load this file.</p>
              ) : (
                <PdfExamViewer src={pdfBlobUrl} title="Submission" className="min-h-[520px]" />
              )
            ) : (
              submissions.length > 0 && (
                <p className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">
                  All uploaded files for this attempt have been deleted.
                </p>
              )
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Evaluation</h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Marks (0 – {attempt.total_marks})
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={attempt.total_marks}
                    step={0.5}
                    value={marksAwarded}
                    onChange={(e) => setMarksAwarded(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Feedback</label>
                  <textarea
                    rows={4}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    placeholder="Optional comments for the student"
                  />
                </div>
                <button
                  onClick={handleSaveEvaluation}
                  disabled={saving}
                  className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Evaluation'}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Corrected Paper</h3>
              <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
                {attempt.has_corrected_paper ? 'A corrected paper has been uploaded.' : 'No corrected paper uploaded yet.'}
              </p>
              {attempt.has_corrected_paper && (
                <button
                  onClick={() => handleAuthDownload(() => facultyExamApi.downloadCorrectedPaper(attemptId), 'corrected_paper.pdf')}
                  className="mb-3 inline-flex items-center gap-2 rounded-md bg-emerald-100 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300"
                >
                  <Download size={16} />
                  Download Current Corrected Paper
                </button>
              )}
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:border-primary dark:border-slate-600 dark:text-slate-300 dark:hover:border-accent-cool">
                <Upload size={16} />
                <span>{reviewedFile?.name || 'Choose corrected paper (PDF)'}</span>
                <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleReviewedFileChange} />
              </label>
              <p className="mt-2"><UploadHint /></p>
              <button
                onClick={handleUploadReviewed}
                disabled={uploadingReviewed || !reviewedFile}
                className="mt-3 w-full rounded-md bg-slate-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {uploadingReviewed ? 'Uploading...' : 'Upload Corrected Paper'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <DeleteSubmissionModal
          attemptNumber={deleteTarget.index + 1}
          deleting={deletingSubmissionId === deleteTarget.id}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteSubmission}
        />
      )}
    </DashboardLayout>
  );
}
